import { env } from '../../utilities/envs';

/**
 * Idle-session configuration.
 *
 * Defaults are tuned for a security-sensitive app (short idle window, brief
 * warning) and can be overridden per deployment via public env vars:
 * - `NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES` — idle time before the warning prompt.
 * - `NEXT_PUBLIC_IDLE_WARNING_SECONDS` — countdown shown before auto sign-out.
 * - `NEXT_PUBLIC_IDLE_TIMEOUT_ENABLED` — set to `'false'` to disable entirely.
 */
export type IdleSessionConfig = {
  /** Milliseconds of inactivity before the keep-alive prompt is shown. */
  readonly idleTimeoutMs: number;
  /** Milliseconds the keep-alive prompt stays open before auto sign-out. */
  readonly warningCountdownMs: number;
  /** When false, idle tracking is disabled. */
  readonly enabled: boolean;
};

const MINUTE_MS = 60 * 1000;
const SECOND_MS = 1000;

/**
 * Idle window before the keep-alive prompt, then the countdown before the
 * automatic sign-out — so a user is signed out after 16 minutes of inactivity.
 *
 * These defaults are what actually ship: nothing sets `NEXT_PUBLIC_IDLE_*`
 * today, neither the gitignored `.env.template-*` files nor the per-environment
 * manifests in the `application-deployments` repo. The env overrides remain
 * available for per-deployment tuning, so a shorter window can be rolled out
 * without a code change.
 */
const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;
const DEFAULT_WARNING_SECONDS = 60;

function parsePositiveNumber(
  value: string | undefined,
  fallback: number,
): number {
  if (!value) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Resolves the idle-session configuration from public env vars with safe defaults. */
export function getIdleSessionConfig(): IdleSessionConfig {
  const idleMinutes = parsePositiveNumber(
    env.NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES,
    DEFAULT_IDLE_TIMEOUT_MINUTES,
  );
  const warningSeconds = parsePositiveNumber(
    env.NEXT_PUBLIC_IDLE_WARNING_SECONDS,
    DEFAULT_WARNING_SECONDS,
  );

  return {
    idleTimeoutMs: idleMinutes * MINUTE_MS,
    warningCountdownMs: warningSeconds * SECOND_MS,
    enabled: env.NEXT_PUBLIC_IDLE_TIMEOUT_ENABLED !== 'false',
  };
}
